// Opening Sync Utilities (CJS, shared by web-app + functions)
//
// Structure openings are canonical for everything except identity, FK,
// and parent-wall membership; room copies hold only those three after
// Phase 5. Structure owns: `center / dimensions / rotation`,
// opening-itself fields (doorType, slidingPanelCount, windowType,
// opening profile), per-side trim + sill, all appearance fields, and
// swing context (wallFacesPriorityRoom, priorityRoomId).
//
// Side A / Side B convention (per-side trim + sill fields):
//   Side A is the +Z face of the opening's local frame.
//   Side B is the -Z face.
//   Which room sits on which side is mediated by `wallFacesPriorityRoom`
//   — relevant for door-swing direction, not for trim rendering.
//   Renderers read per-side fields by name and draw each face
//   independently; they do not consult `wallFacesPriorityRoom` to decide
//   which side to render.
//
// Mirrors the web-app ESM module at `web-app/src/utils/openingSyncUtils.js`.
// Both files MUST be kept in lockstep — when changing one, mirror the
// other. The web-app file is the authoritative source for behavior; this
// CJS copy exists so functions/-side code (design-automation tools,
// migrations) can require these helpers without an ESM↔CJS interop hop.

// All door/window styling lives on structure post-Phase 5. Room copies
// carry only identity + FK + parentWallIds — no trim, no appearance.
const DOOR_TRIM_FIELDS = [];
const WINDOW_TRIM_FIELDS = [];

const TRIM_FIELDS_BY_CATEGORY = {
  door: DOOR_TRIM_FIELDS,
  window: WINDOW_TRIM_FIELDS,
  opening: [],
};

// Post-Phase-5: all styling — including per-side trim/sill — lives on
// structure. APPEARANCE drives the structure-side write filter.
const APPEARANCE_FIELDS_BY_CATEGORY = {
  door: [
    'style',
    'doorColor',
    'handleStyle',
    'handleColor',
    'hasThreshold',
    'trimStyleSideA',
    'trimStyleSideB',
    'trimColorSideA',
    'trimColorSideB',
  ],
  window: [
    'style',
    'panes',
    'frameColor',
    'addHeader',
    'trimStyleSideA',
    'trimStyleSideB',
    'trimColorSideA',
    'trimColorSideB',
    'trimWidthSideA',
    'trimWidthSideB',
    'trimDepthSideA',
    'trimDepthSideB',
    'sillDepthSideA',
    'sillDepthSideB',
    'sillColorSideA',
    'sillColorSideB',
  ],
  opening: [],
};

function pickFields(obj, fields) {
  if (!obj) return {};
  const out = {};
  for (const field of fields) {
    if (field in obj && obj[field] !== undefined) {
      out[field] = obj[field];
    }
  }
  return out;
}

function isRoomCopyOf(roomElement, structureId) {
  return (
    roomElement?.id === structureId ||
    roomElement?.structureOpeningId === structureId
  );
}

function buildOpeningLookup({ doors = [], windows = [], openings = [] } = {}) {
  const map = new Map();
  for (const d of doors) if (d?.id) map.set(d.id, d);
  for (const w of windows) if (w?.id) map.set(w.id, w);
  for (const o of openings) if (o?.id) map.set(o.id, o);
  return map;
}

// Hydrate a room's slim door/window/opening arrays into resolved copies
// (full geometry + appearance) using a structure-side lookup. The room
// copy keeps its own id / structureOpeningId / parentWallIds; everything
// else comes from the structure copy. Use this at the boundary of any
// pipeline that reads `room.doors[i].center` / `dimensions` (kitchen
// layout walkway / clearance / wall-segment subtraction).
function hydrateRoomOpenings(
  room,
  { doors = [], windows = [], openings = [] } = {}
) {
  if (!room) return room;
  const lookup = buildOpeningLookup({ doors, windows, openings });
  if (lookup.size === 0) return room;
  const resolve = (arr) =>
    (arr || []).map((o) =>
      resolveRoomOpening(o, lookup.get(o?.structureOpeningId))
    );
  return {
    ...room,
    doors: resolve(room.doors),
    windows: resolve(room.windows),
    openings: resolve(room.openings),
  };
}

/**
 * The engine-facing ROOM-frame opening list: every door / window / opening
 * copy the room holds, hydrated against the canonical structure arrays
 * (geometry from the structure copy, wall association from the ROOM copy)
 * and tagged with the `_kind` the cabinet-run engine's clearance zones
 * dispatch on. A copy that resolves to nothing stays slim and is DROPPED —
 * handing the engine an opening it would measure as zero-width at the
 * origin is worse than omitting it.
 *
 * This is THE frame join for anything that matches openings to walls by
 * strict id (`shared-lib/cabinet-runs/openings.js` compares
 * `parentWallIds[0] === wall.id`): passing the structure-frame arrays
 * straight through makes every comparison false, silently — the bug class
 * of #1499 and its server twin #1552.
 */
function roomFrameOpenings(
  room,
  { doors = [], windows = [], openings = [] } = {}
) {
  const hydrated = hydrateRoomOpenings(room, { doors, windows, openings });
  const out = [];
  for (const [field, kind] of [
    ['doors', 'door'],
    ['windows', 'window'],
    ['openings', 'opening'],
  ]) {
    for (const o of hydrated?.[field] || []) {
      if (!o?.center || !o?.dimensions || !(o.parentWallIds || [])[0]) continue;
      out.push({ ...o, _kind: kind });
    }
  }
  return out;
}

function resolveRoomOpening(roomOpening, structureOpening) {
  if (!structureOpening) return roomOpening;

  const categoryKey = Object.keys(structureOpening.category || {})[0];
  if (!categoryKey) return roomOpening;

  const trimFields = TRIM_FIELDS_BY_CATEGORY[categoryKey] || [];
  const roomCategory = roomOpening?.category?.[categoryKey] || {};
  const roomTrim = pickFields(roomCategory, trimFields);

  const styleKey =
    categoryKey === 'door'
      ? 'doorStyle'
      : categoryKey === 'window'
        ? 'windowStyle'
        : null;
  const legacyStyle = styleKey ? roomCategory[styleKey] : null;
  const legacyTrim = legacyStyle ? pickFields(legacyStyle, trimFields) : {};
  const effectiveRoomTrim = { ...legacyTrim, ...roomTrim };

  // P5 transitional: structure-side appearance used to live nested in
  // doorStyle / windowStyle. Hoist on-the-fly until migrate-opening-shape's
  // appearance-hoist pass has swept legacy data. The hoist only FILLS what
  // the flat fields lack — every writer writes flat, so the nested copy is
  // the older one (the mirror in roomOpeningHydration.js says more).
  const structureCategory = structureOpening.category[categoryKey] || {};
  const structureNestedStyle = styleKey ? structureCategory[styleKey] : null;
  const appearanceFields = APPEARANCE_FIELDS_BY_CATEGORY[categoryKey] || [];
  const legacyStructureAppearance = structureNestedStyle
    ? pickFields(structureNestedStyle, appearanceFields)
    : {};

  const legacySingleTrim = pickLegacySideExpansion(roomCategory, categoryKey);

  return {
    ...structureOpening,
    id: roomOpening?.id,
    structureOpeningId: roomOpening?.structureOpeningId,
    parentWallIds: roomOpening?.parentWallIds,
    category: {
      ...structureOpening.category,
      [categoryKey]: {
        ...legacyStructureAppearance,
        ...structureCategory,
        ...legacySingleTrim,
        ...effectiveRoomTrim,
      },
    },
  };
}

// Symmetric (both sides) expansion — trim should default identical on both
// faces unless the user explicitly overrides per-side.
const LEGACY_SINGLE_TRIM_BY_CATEGORY = {
  door: ['trimStyle'],
  window: ['trimStyle', 'trimColor', 'trimWidth', 'trimDepth'],
  opening: [],
};
// Exterior-only (Side B) expansion — legacy single sill maps to an exterior
// weather sill, matching the pre-Phase-5 single-sill orientation (lone sill
// at -Z with bevel facing outside). An interior stool requires explicit
// per-side authoring.
const LEGACY_SINGLE_SILL_BY_CATEGORY = {
  door: [],
  window: ['sillDepth', 'sillColor'],
  opening: [],
};

function pickLegacySideExpansion(roomCategory, categoryKey) {
  const out = {};
  const trim = LEGACY_SINGLE_TRIM_BY_CATEGORY[categoryKey] || [];
  for (const f of trim) {
    if (roomCategory?.[f] === undefined) continue;
    const v = roomCategory[f];
    out[`${f}SideA`] = v;
    out[`${f}SideB`] = v;
  }
  const sill = LEGACY_SINGLE_SILL_BY_CATEGORY[categoryKey] || [];
  for (const f of sill) {
    if (roomCategory?.[f] === undefined) continue;
    out[`${f}SideB`] = roomCategory[f];
  }
  return out;
}

function applyAppearanceToStructureOpening(structureOpening, settings, type) {
  const categoryKey = type;
  const allowed = APPEARANCE_FIELDS_BY_CATEGORY[categoryKey] || [];
  const appearance = pickFields(settings, allowed);

  return {
    ...structureOpening,
    category: {
      ...structureOpening.category,
      [categoryKey]: {
        ...structureOpening.category?.[categoryKey],
        ...appearance,
      },
    },
  };
}

function applyTrimToRoomOpening(roomOpening, settings, type) {
  const categoryKey = type;
  const allowed = TRIM_FIELDS_BY_CATEGORY[categoryKey] || [];
  const trim = pickFields(settings, allowed);

  return {
    ...roomOpening,
    category: {
      ...roomOpening?.category,
      [categoryKey]: {
        ...roomOpening?.category?.[categoryKey],
        ...trim,
      },
    },
  };
}

// A demoed wall is an opening subtype: category.opening.demoed === true.
// (The legacy `category.demoedWall` sibling shape was retired by the
// migrate-demoed-wall-subtype data migration.)
function isDemoedOpening(item) {
  return item?.category?.opening?.demoed === true;
}

/**
 * True when an opening carries geometry something can actually be planned or
 * drawn against. The twin of `hasRenderableOpeningGeometry` in
 * `web-app/src/utils/openingSyncUtils.js` — keep the rule identical.
 *
 * `resolveRoomOpening` returns a room's SLIM copy unchanged when the structure
 * lookup misses, so an unresolvable opening arrives with no `center` at all.
 * Anything downstream that defaults the missing fields then invents geometry —
 * `extractWindows` fabricates `center {0, 1.5, 0}` — which is how a curtain got
 * planned against a phantom window at the world origin.
 */
function openingHasRealGeometry(opening) {
  const finite = (v) => typeof v === 'number' && Number.isFinite(v);
  const c = opening?.center;
  if (!c || !finite(c.x) || !finite(c.y) || !finite(c.z)) return false;
  // Arrays and `{x,y,z}` both occur.
  const d = opening?.dimensions;
  const [w, h] = Array.isArray(d) ? [d[0], d[1]] : [d?.x, d?.y];
  return finite(w) && finite(h) && w > 0 && h > 0;
}

/**
 * A hydrated room with every WINDOW or generic opening that did not resolve to
 * real geometry dropped. For CONSUMERS that read opening geometry (the dorm
 * decor planners); never for the sync paths, which need to see an unresolved
 * copy to repair it.
 *
 * **Doors are deliberately kept, resolved or not.** The reason to drop an
 * unresolved opening is that `extractWindows` invents geometry for it and the
 * planners then hang a curtain or seat a sill plant against a window that is
 * not there. Doors have no such consumer: every one of them attributes a door
 * to a WALL rather than reading its position — `doorWallIdSet` and
 * `isOpeningOnWall` match on `parentWallIds` first and only fall back to
 * `door.center`, and the wall-span pass blocks the WHOLE wall for a
 * centre-less door attributed to it.
 *
 * Dropping them therefore removed information instead of bad geometry:
 * `cornerTouchesDoor` stopped rejecting the doorway corners, and floor decor
 * (plants, pet bed) started being placed in the doorway. That attribution
 * works fine on a slim copy — measured over the corpus, 821 of 821 unresolved
 * openings carry a `parentWallIds` matching a wall on their own room doc.
 *
 * Note the asymmetry: for windows the filter makes placement more
 * conservative, for doors it made it LESS.
 */
function withResolvedOpeningsOnly(room) {
  if (!room) return room;
  const keep = (arr) =>
    Array.isArray(arr) ? arr.filter(openingHasRealGeometry) : arr;
  return {
    ...room,
    windows: keep(room.windows),
    openings: keep(room.openings),
  };
}

module.exports = {
  DOOR_TRIM_FIELDS,
  WINDOW_TRIM_FIELDS,
  TRIM_FIELDS_BY_CATEGORY,
  APPEARANCE_FIELDS_BY_CATEGORY,
  pickFields,
  isRoomCopyOf,
  buildOpeningLookup,
  resolveRoomOpening,
  hydrateRoomOpenings,
  roomFrameOpenings,
  applyAppearanceToStructureOpening,
  applyTrimToRoomOpening,
  isDemoedOpening,
  openingHasRealGeometry,
  withResolvedOpeningsOnly,
};
